import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type PlanType = "starter" | "professional";
export type PlanStatus = "active" | "cancelled" | "past_due" | "trialing";

export interface NurseryPlan {
  plan: PlanType;
  plan_status: PlanStatus;
  plan_ends_at: string | null;
  name: string;
  trial_required?: boolean;
}

function getEffectivePlan(data: NurseryPlan | null): PlanType {
  if (!data) return "starter";
  // Expired new-cohort accounts retain their existing Professional views/exports.
  // Server-side write guards enforce their read-only state.
  if (data.trial_required) return "professional";

  if (data.plan === "professional" && data.plan_status === "cancelled") {
    if (data.plan_ends_at && new Date(data.plan_ends_at) > new Date()) {
      return "professional"; // Grace period
    }
    return "starter";
  }

  return data.plan;
}

export function usePlan() {
  const { user } = useAuth();

  const { data: nurseryData, isLoading } = useQuery({
    queryKey: ["nursery-plan", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("nurseries")
        .select("plan, plan_status, plan_ends_at, name, trial_required")
        .single();

      if (error || !data) return null;
      return data as NurseryPlan;
    },
    enabled: !!user,
    staleTime: 60_000, // Re-check every minute
    refetchOnWindowFocus: true,
  });

  const effectivePlan = getEffectivePlan(nurseryData ?? null);
  const isProfessional = effectivePlan === "professional";
  const isStarter = effectivePlan === "starter";
  const isPastDue = nurseryData?.plan_status === "past_due";

  return {
    plan: effectivePlan,
    planStatus: nurseryData?.plan_status ?? "active",
    nurseryName: nurseryData?.name ?? "",
    planEndsAt: nurseryData?.plan_ends_at ?? null,
    isProfessional,
    isStarter,
    isPastDue,
    isLoading,
  };
}

// Features locked on Starter plan
export const PROFESSIONAL_ONLY_ROUTES = [
  "/compliance",
  "/ofsted-readiness",
  "/attendance-staffing",
  "/funding",
  "/reports",
  "/alerts",
  "/pickups/today",
  "/pickups/monthly",
  "/pickups/schools",
  "/pickups/bookings",
  "/audit-log",
  "/additional-days",
  "/safeguarding-log",
  "/confidential-safeguarding",
  "/transitions",
];


export function isRouteLocked(path: string, plan: PlanType): boolean {
  if (plan === "professional") return false;
  return PROFESSIONAL_ONLY_ROUTES.some(r => path.startsWith(r));
}
